import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import './Calendar.scss';
import 'react-big-calendar/lib/css/react-big-calendar.css';
const calendar = '/icons/calendar.svg';

import CalendarHeader from './factory/CalendarHeader';
import Text from '../../atoms/text/Text';
import useIsRtl from '../../../app/helpers/isRtl';
moment.updateLocale('en', { week: { dow: 1, doy: 4 } });
moment.updateLocale('ar', { week: { dow: 1, doy: 4 } });
moment.updateLocale(moment.locale(), { week: { dow: 1, doy: 4 } });

const localizer = momentLocalizer(moment);

const EventIndicator = ({ color, text }) => {
  return (
    <div className="event-date">
      <div
        className="event-indicator"
        style={{
          backgroundColor: color,
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          flexShrink: 0,
        }}
      ></div>
      <Text title={text} className="text" variant="text-regular-16-black" />
    </div>
  );
};

const transformEventsForCalendar = (rawData) => {
  return rawData.flatMap((monthData) =>
    monthData.calendarDays.map((event) => ({
      title: event.calendarText,
      color: event.colorCode.trim().split(' ')[0],
      start: new Date(event.fromDate),
      end: new Date(event.endDate),
    }))
  );
};

const CalendarUI = ({ data }) => {
  const isRtl = useIsRtl();
  const [events, setEvents] = useState([]);
  const [view, setView] = useState(Views.MONTH);
  const [selectedDate, setSelectedDate] = useState(null);
  const firstMonthData = data?.[0];

  const initialMonth = useMemo(() => {
    if (firstMonthData?.monthInt && firstMonthData?.yearInt) {
      return new Date(firstMonthData.yearInt, firstMonthData.monthInt - 1, 1);
    }
    return new Date();
  }, [data]);

  const [currentMonth, setCurrentMonth] = useState(initialMonth);

  useEffect(() => {
    setEvents(transformEventsForCalendar(data));
  }, [data]);

  const onNavigate = (newDate) => {
    const startOfYear = new Date(2023, 0);
    const endOfYear = new Date(2024, 11, 31);
    if (newDate >= startOfYear && newDate <= endOfYear) {
      setCurrentMonth(newDate);
    }
  };

  const eventStyleGetter = (event) => {
    const backgroundColor = event.color || '#3174ad';
    const style = {
      backgroundColor,
      borderRadius: '0px',
      opacity: 0.8,
      color: 'black',
      border: '0px',
      display: 'block',
      minHeight: '20px',
    };
    return { style };
  };

  const eventsForCurrentMonth = events.filter(
    (event) =>
      new Date(event.start).getMonth() === currentMonth.getMonth() &&
      new Date(event.start).getFullYear() === currentMonth.getFullYear()
  );

  const handleEventClick = (event) => {
    setSelectedDate(new Date(event.start));
    setView(Views.DAY);
  };

  const handleBackToMonth = () => {
    setSelectedDate(null);
    setView(Views.MONTH);
  };

  const today = new Date();
  const dayName = today.toLocaleDateString('ar', { weekday: 'long' });
  const date = today.getDate();
  const monthYear = today.toLocaleDateString('ar', {
    year: 'numeric',
    month: 'long',
  });

  return (
    <div className="calendar-container">
      <div className="calendar">
        <CalendarHeader
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          eventsForCurrentMonth={eventsForCurrentMonth}
        />
        {view === Views.MONTH ? (
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            className="calendarUI"
            toolbar={false}
            onNavigate={onNavigate}
            date={currentMonth}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleEventClick}
          />
        ) : (
          <>
            <div className="back-button">
              <Text
                title="common.back_to_month_view"
                variant="text-bold-18"
                style={{ textTransform: 'capitalized' }}
                color="#1a1a1a"
                onClick={handleBackToMonth}
              />
            </div>

            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              className="calendarUI"
              toolbar={false}
              date={selectedDate}
              view={Views.DAY}
              eventPropGetter={eventStyleGetter}
              onNavigate={onNavigate}
            />
          </>
        )}
      </div>

      <div className="side-calendar">
        <div className="side-calendar-header">
          <div className="header-top">
            <div className="header-day">
              <div className="header-today">
                <Text title={date} variant="text-bold-italic-70" />
                <div className="header-tiday-subtitle">
                  <Text
                    title={dayName}
                    variant="text-regular-14"
                    className="today-text"
                  />
                  <Text
                    title={monthYear}
                    variant="text-regular-14"
                    className="today-text"
                  />
                </div>
              </div>
              <div className="header-today-svgs">
                <Text title="Filter.day" variant="text-regular-14" />{' '}
                <img src={calendar} />
              </div>
            </div>
          </div>
          <div className="header-bottom">
            <div className="header-bottom-events">
              <Text
                title="academic_calendar.all_month_events"
                variant="text-bold-18"
                style={{ textTransform: 'uppercase' }}
              />
              <Text
                title={eventsForCurrentMonth.length.toString()}
                variant="text-bold-18"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            {/* <div className="header-bottom-day">
              <Text
                title="academic_calendar.today"
                variant="text-regular-18"
                style={{ textTransform: 'uppercase' }}
                className="today-text"
              />
              <Text
                title="1"
                variant="text-regular-18"
                style={{ textTransform: 'uppercase' }}
                className="today-text"
              />
            </div> */}
          </div>
        </div>
        <div
          className="side-calendar-body"
          style={{ direction: isRtl ? 'rtl' : 'ltr' }}
        >
          {eventsForCurrentMonth.map((event, index) => (
            <EventIndicator
              key={index}
              text={event.title}
              color={event.color}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarUI;
